import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    UploadCloud, Plus, ArrowUp, ArrowDown, Trash2, Download, RotateCcw,
    Loader2, ShieldCheck, AlertTriangle, CheckCircle2, Circle, CircleDashed, Ban, Save,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { PageGrid } from "../components/PageGrid";
import { getFlowOperations, createFlowJob, getJob, downloadUrl, errorMessage, saveToHistory } from "../lib/api";
import { usePro } from "../context/ProContext";
import { useFileEntries } from "../lib/filelist";
import { FileSortList } from "../components/FileSortList";

const FLOW_VISUAL = { girar: "rotate", reordenar: "reorder", "remover-paginas": "remove" };

const STEP_ICON = {
    queued: CircleDashed,
    validating: Loader2,
    processing: Loader2,
    done: CheckCircle2,
    failed: AlertTriangle,
    cancelled: Ban,
};

const STEP_LABEL = {
    queued: "aguardando",
    validating: "validando",
    processing: "processando",
    done: "concluída",
    failed: "falhou",
    cancelled: "cancelada",
};

function formatBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function newIdemKey() {
    return (crypto.randomUUID && crypto.randomUUID()) || `k-${Date.now()}-${Math.random()}`;
}

export default function FlowPage() {
    const [catalog, setCatalog] = useState(null);
    const [catalogError, setCatalogError] = useState(null);
    const [ops, setOps] = useState([]);
    const [phase, setPhase] = useState("idle"); // idle | uploading | running | done | failed
    const [progress, setProgress] = useState(0);
    const [job, setJob] = useState(null);
    const [token, setToken] = useState(null);
    const [error, setError] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [historySaved, setHistorySaved] = useState(false);
    const [historySaving, setHistorySaving] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const { isPro } = usePro();
    const inputRef = useRef(null);
    const pollRef = useRef(null);
    const idemKey = useRef(newIdemKey());
    const runSeqRef = useRef(0);      // numero da execucao vigente (resposta atrasada de run antigo e ignorada)
    const activeJobRef = useRef(null); // job_id da execucao viva no polling

    useEffect(() => {
        getFlowOperations()
            .then(setCatalog)
            .catch((e) => setCatalogError(errorMessage(e)));
        return () => clearInterval(pollRef.current);
    }, []);

    const limits = catalog?.limits;
    const usage = catalog?.usage;
    const limitReached = usage && usage.used >= usage.limit;

    const fl = useFileEntries({
        maxFiles: limits?.max_files ?? 10,
        maxFileMB: limits?.max_file_mb ?? 25,
        multi: true,
    });

    const refreshUsage = () => getFlowOperations().then(setCatalog).catch(() => {});

    const addFiles = (list) => {
        setError(null);
        fl.addFiles(list);
        invalidateRun();
    };

    // ---- gerenciamento das etapas ----
    // Qualquer mudanca no fluxo (etapas ou arquivos) invalida a execucao anterior:
    // para o polling, descarta resultado/erro antigos, renova a chave de idempotencia
    // e devolve o construtor ao estado editavel. Se houver processamento em curso,
    // ele continua no servidor (expira sozinho), mas o resultado e descartado aqui.
    const invalidateRun = () => {
        runSeqRef.current += 1;
        activeJobRef.current = null;
        clearInterval(pollRef.current);
        setJob(null);
        setToken(null);
        setError(null);
        setHistorySaved(false);
        setHistorySaving(false);
        setHistoryError(null);
        setPhase("idle");
        idemKey.current = newIdemKey();
    };

    const addOp = (key) => {
        const spec = catalog.operations.find((o) => o.key === key);
        const defaults = Object.fromEntries(
            (spec.options || []).filter((o) => o.default !== undefined).map((o) => [o.key, o.default])
        );
        setOps((prev) => [...prev, { id: newIdemKey(), tool: key, options: defaults }]);
        setAddOpen(false);
        invalidateRun();
    };
    const removeOp = (id) => {
        setOps((prev) => prev.filter((o) => o.id !== id));
        invalidateRun();
    };
    const moveOp = (id, dir) => {
        setOps((prev) => {
            const i = prev.findIndex((o) => o.id === id);
            const j = i + dir;
            if (i < 0 || j < 0 || j >= prev.length) return prev;
            const next = [...prev];
            [next[i], next[j]] = [next[j], next[i]];
            return next;
        });
        invalidateRun();
    };
    const setOpOption = (id, key, value) => {
        setOps((prev) => prev.map((o) => (o.id === id ? { ...o, options: { ...o.options, [key]: value } } : o)));
        invalidateRun();
    };

    // quais operacoes podem ser adicionadas na posicao atual
    const availableToAdd = () => {
        if (!catalog) return [];
        const hasTerminal = ops.length > 0 && catalog.operations.find((o) => o.key === ops[ops.length - 1].tool)?.position === "last";
        return catalog.operations.filter((spec) => {
            if (!spec.available) return false;
            if (hasTerminal) return false;
            if (spec.position === "first") return ops.length === 0;
            return true;
        });
    };

    const clientWarnings = () => {
        if (!catalog || ops.length === 0) return [];
        const warns = [];
        const firstSpec = catalog.operations.find((o) => o.key === ops[0].tool);
        if (firstSpec?.input_kind === "pdfs" && fl.entries.length > 0 && fl.entries.length < 2)
            warns.push("'Juntar PDFs' exige pelo menos 2 arquivos.");
        if (firstSpec && firstSpec.input_kind === "pdf" && fl.entries.length > 1)
            warns.push("Este fluxo começa com um único PDF — você enviou vários arquivos. Comece com 'Juntar PDFs'.");
        const lastSpec = catalog.operations.find((o) => o.key === ops[ops.length - 1].tool);
        if (lastSpec?.position === "last")
            warns.push(`'${lastSpec.label}' é a etapa final: o resultado será ${lastSpec.key === "dividir" ? "um ou mais PDFs (ZIP quando houver vários)" : "um pacote de imagens (ZIP)"}.`);
        return warns;
    };

    // ---- processamento ----
    const startPolling = (jobId, token, runSeq) => {
        clearInterval(pollRef.current);
        activeJobRef.current = jobId;
        pollRef.current = setInterval(async () => {
            // execucao descartada (etapa removida/arquivo trocado): encerra sem tocar o estado
            if (activeJobRef.current !== jobId || runSeqRef.current !== runSeq) {
                clearInterval(pollRef.current);
                return;
            }
            try {
                const data = await getJob(jobId, token);
                // resposta atrasada de uma execucao que ja foi descartada: ignora
                if (activeJobRef.current !== jobId || runSeqRef.current !== runSeq) return;
                setJob(data);
                if (["done", "failed", "expired"].includes(data.status)) {
                    clearInterval(pollRef.current);
                    activeJobRef.current = null;
                    setPhase(data.status === "done" ? "done" : "failed");
                    if (data.status !== "done") setError(data.error);
                    // estado terminal: a proxima tentativa e um job NOVO (nunca reabre o job antigo)
                    idemKey.current = newIdemKey();
                    refreshUsage();
                }
            } catch {
                if (activeJobRef.current !== jobId || runSeqRef.current !== runSeq) return;
                clearInterval(pollRef.current);
                activeJobRef.current = null;
                setPhase("failed");
                setError("Perdemos a conexão com o servidor. Tente novamente.");
                idemKey.current = newIdemKey();
            }
        }, 1200);
    };

    const process = async () => {
        const mySeq = ++runSeqRef.current;
        setError(null);
        setPhase("uploading");
        setProgress(0);
        setJob(null);
        try {
            const seq = fl.uploadSequence();
            const created = await createFlowJob(
                seq.files,
                ops.map((o) => ({ tool: o.tool, options: o.options })),
                idemKey.current,
                (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
                { ids: seq.ids, order: seq.ids, names: seq.names }
            );
            // o fluxo mudou enquanto o upload acontecia: descarta esta execucao
            if (runSeqRef.current !== mySeq) return;
            setJob(created);
            setToken(created.token);
            setPhase("running");
            startPolling(created.job_id, created.token, mySeq);
        } catch (err) {
            if (runSeqRef.current !== mySeq) return;
            setPhase("failed");
            setError(errorMessage(err));
            idemKey.current = newIdemKey();
            refreshUsage();
        }
    };

    const resetAll = () => {
        clearInterval(pollRef.current);
        runSeqRef.current += 1;
        activeJobRef.current = null;
        fl.reset();
        setOps([]); setJob(null); setToken(null); setError(null);
        setHistorySaved(false); setHistorySaving(false); setHistoryError(null);
        setPhase("idle"); idemKey.current = newIdemKey();
    };

    const saveHistory = async () => {
        if (historySaving || historySaved || !job || !token) return;
        setHistorySaving(true);
        setHistoryError(null);
        try {
            await saveToHistory(job.job_id, token);
            setHistorySaved(true);
        } catch (err) {
            setHistoryError(errorMessage(err));
        } finally {
            setHistorySaving(false);
        }
    };

    // corrige opcoes e reenvia MANTENDO os arquivos ja selecionados
    const retryFlow = () => {
        clearInterval(pollRef.current);
        runSeqRef.current += 1;
        activeJobRef.current = null;
        setJob(null); setToken(null); setError(null); setPhase("idle");
        idemKey.current = newIdemKey();
    };

    const busy = phase === "uploading" || phase === "running";
    const canProcess = fl.entries.length > 0 && ops.length > 0 && !busy && !limitReached;

    // mudancas na lista de arquivos (remover, arrastar, A-Z/Z-A) tambem invalidam a execucao
    const flGuarded = {
        ...fl,
        remove: (id) => { invalidateRun(); fl.remove(id); },
        reorder: (ids) => { invalidateRun(); fl.reorder(ids); },
        restoreOriginal: () => { invalidateRun(); fl.restoreOriginal(); },
        setSortMode: (m) => { if (m !== fl.sortMode) invalidateRun(); fl.setSortMode(m); },
    };

    return (
        <main className="py-12 px-6 max-w-4xl mx-auto min-h-[60vh]">
            <Seo
                title="Combinar ferramentas de PDF em um fluxo — ScorpionPDF"
                description="Monte um fluxo com várias operações de PDF em sequência — juntar, remover páginas, comprimir, converter para PDF/A — e baixe apenas o resultado final."
            />
            <span className="font-mono-tag text-xs font-bold text-[#0284C7] uppercase">Módulo avançado</span>
            <h1 className="font-poster text-4xl md:text-6xl text-black mt-1">Combinar ferramentas</h1>
            <p className="text-gray-700 mt-3 max-w-2xl">
                Encadeie várias operações em um único fluxo: o resultado de uma etapa alimenta a próxima, sem downloads
                intermediários. Um fluxo inteiro conta como <strong>1 operação</strong> do seu limite diário.
            </p>

            {usage && (
                <p className="mt-3 font-mono-tag text-xs font-bold uppercase" data-testid="usage-badge">
                    Hoje: {usage.used}/{usage.limit} operações ({usage.plan === "pro" ? "Plano Pro" : "Plano Grátis"})
                    {limitReached && (
                        <span className="text-red-700"> — limite atingido. <Link to="/planos" className="underline">Ver Pro</Link></span>
                    )}
                </p>
            )}

            {catalogError && (
                <div className="mt-6 border-2 border-black bg-[#FEF2F2] p-4 text-sm text-red-800" role="alert" data-testid="flow-catalog-error">
                    Não foi possível carregar as operações: {catalogError}
                </div>
            )}

            {/* 1. ARQUIVOS */}
            <section className="mt-8 border-2 border-black bg-white hard-shadow" aria-labelledby="flow-files">
                <h2 id="flow-files" className="px-4 pt-4 font-poster text-xl text-black">1 · Arquivos de entrada</h2>
                <div
                    role="button" tabIndex={0}
                    aria-label="Enviar arquivos para o fluxo"
                    data-testid="flow-dropzone"
                    onClick={() => !busy && inputRef.current?.click()}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !busy && inputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); if (!busy) addFiles(e.dataTransfer.files); }}
                    className={`m-4 border-2 border-dashed border-gray-400 hover:border-black p-6 text-center cursor-pointer transition-colors ${busy ? "opacity-60 cursor-wait" : ""}`}
                >
                    <UploadCloud className="w-8 h-8 mx-auto text-black mb-2" aria-hidden="true" />
                    <span className="font-poster text-xl text-black block">ARRASTE PDFs OU IMAGENS</span>
                    <span className="font-mono-tag text-xs text-gray-500">
                        {limits ? `até ${limits.max_file_mb} MB · máx. ${limits.max_files} arquivos` : "carregando limites…"}
                    </span>
                    <input
                        ref={inputRef} type="file" multiple className="hidden"
                        accept=".pdf,application/pdf,image/*"
                        data-testid="flow-file-input"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                    />
                </div>
                {fl.entries.length > 0 && (
                    <FileSortList fl={flGuarded} disabled={busy} prefix="flow-" />
                )}
                {fl.listError && (
                    <p className="mx-4 mb-4 border-2 border-black bg-[#FEF2F2] px-3 py-2 text-xs text-red-800 font-medium" role="alert" data-testid="flow-list-error">
                        {fl.listError}
                    </p>
                )}
            </section>

            {/* 2. ETAPAS */}
            <section className="mt-6 border-2 border-black bg-white hard-shadow" aria-labelledby="flow-steps">
                <div className="px-4 pt-4 flex items-center justify-between gap-3 flex-wrap">
                    <h2 id="flow-steps" className="font-poster text-xl text-black">2 · Etapas do fluxo</h2>
                    {!busy && (
                        <button
                            onClick={() => setAddOpen(!addOpen)}
                            data-testid="add-operation-button"
                            aria-expanded={addOpen}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-black text-[#22D3EE] font-mono-tag text-xs font-bold uppercase hover:bg-[#0284C7] hover:text-white transition-colors"
                        >
                            <Plus className="w-4 h-4" aria-hidden="true" /> Adicionar operação
                        </button>
                    )}
                </div>

                {addOpen && catalog && (
                    <div className="m-4 border-2 border-black bg-[#FAFAF9] p-3 grid sm:grid-cols-2 gap-2" data-testid="operation-picker">
                        {availableToAdd().map((spec) => (
                            <button
                                key={spec.key}
                                onClick={() => addOp(spec.key)}
                                data-testid={`add-op-${spec.key}`}
                                className="text-left border-2 border-black bg-white p-3 hover:bg-[#F0F9FF] transition-colors"
                            >
                                <span className="font-bold text-sm text-black block">{spec.label}</span>
                                <span className="text-xs text-gray-600">{spec.description}</span>
                            </button>
                        ))}
                        {availableToAdd().length === 0 && (
                            <p className="text-xs text-gray-600 p-2">Nenhuma operação pode ser adicionada nesta posição do fluxo.</p>
                        )}
                    </div>
                )}

                {ops.length === 0 && !addOpen && (
                    <p className="px-4 pb-4 pt-2 text-sm text-gray-600" data-testid="flow-empty-steps">
                        Nenhuma operação ainda. Adicione a primeira — por exemplo, “Juntar PDFs”.
                    </p>
                )}

                <ol className="p-4 space-y-3" data-testid="flow-steps-list">
                    {ops.map((op, i) => {
                        const spec = catalog?.operations.find((o) => o.key === op.tool);
                        const stepState = job?.steps?.[i];
                        const Icon = stepState ? STEP_ICON[stepState.status] || Circle : Circle;
                        return (
                            <li
                                key={op.id}
                                className={`border-2 p-4 transition-colors ${
                                    stepState?.status === "failed"
                                        ? "border-red-700 bg-[#FEF2F2]"
                                        : stepState?.status === "done"
                                          ? "border-black bg-[#F0F9FF]"
                                          : "border-black bg-[#FAFAF9]"
                                }`}
                                data-testid={`flow-step-${i}`}
                            >
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className="font-poster text-2xl text-[#0284C7]">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="font-bold text-black flex-1">{spec?.label || op.tool}</span>
                                    {stepState && (
                                        <span
                                            className={`inline-flex items-center gap-1.5 font-mono-tag text-[11px] font-bold uppercase ${
                                                stepState.status === "failed" ? "text-red-700" : stepState.status === "done" ? "text-[#0284C7]" : "text-gray-600"
                                            }`}
                                            data-testid={`flow-step-status-${i}`}
                                        >
                                            <Icon className={`w-4 h-4 ${["validating", "processing"].includes(stepState.status) ? "animate-spin" : ""}`} aria-hidden="true" />
                                            {STEP_LABEL[stepState.status]}
                                        </span>
                                    )}
                                    {!busy && phase !== "done" && (
                                        <span className="flex items-center gap-1">
                                            <button onClick={() => moveOp(op.id, -1)} disabled={i === 0} aria-label={`Mover etapa ${i + 1} para cima`} data-testid={`step-up-${i}`} className="p-1.5 border-2 border-black bg-white disabled:opacity-30 hover:bg-black hover:text-white transition-colors">
                                                <ArrowUp className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => moveOp(op.id, 1)} disabled={i === ops.length - 1} aria-label={`Mover etapa ${i + 1} para baixo`} data-testid={`step-down-${i}`} className="p-1.5 border-2 border-black bg-white disabled:opacity-30 hover:bg-black hover:text-white transition-colors">
                                                <ArrowDown className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => removeOp(op.id)} aria-label={`Remover etapa ${i + 1}`} data-testid={`step-remove-${i}`} className="p-1.5 border-2 border-black bg-white hover:bg-red-700 hover:text-white hover:border-red-700 transition-colors">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </span>
                                    )}
                                    {/* remover SEMPRE disponivel fora de "done": durante o processamento,
                                        remover a etapa descarta a execucao em curso com seguranca */}
                                    {busy && phase !== "done" && (
                                        <button onClick={() => removeOp(op.id)} aria-label={`Remover etapa ${i + 1} (cancela o processamento em curso)`} title="Remover etapa e descartar o processamento em curso" data-testid={`step-remove-${i}`} className="p-1.5 border-2 border-black bg-white hover:bg-red-700 hover:text-white hover:border-red-700 transition-colors">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                                {spec?.warning && (
                                    <p className="mt-2 text-[11px] text-gray-600 border-l-2 border-[#0284C7] pl-2" data-testid={`step-warning-${i}`}>
                                        {spec.warning}
                                    </p>
                                )}
                                {stepState?.status === "failed" && stepState.error && (
                                    <p className="mt-2 text-xs font-bold text-red-800" data-testid={`step-error-${i}`}>{stepState.error}</p>
                                )}
                                {!busy && phase !== "done" && (spec?.options || []).length > 0 && (
                                    <div className="mt-3 space-y-3">
                                        {/* grade visual: 1a etapa com PDF unico — paginas batem com o enviado */}
                                        {i === 0 && FLOW_VISUAL[op.tool] && fl.entries.length === 1 &&
                                        (fl.entries[0].type === "application/pdf" || fl.entries[0].name.toLowerCase().endsWith(".pdf")) ? (
                                            <div data-testid={`flow-visual-${i}`}>
                                                <PageGrid
                                                    file={fl.entries[0].file}
                                                    mode={FLOW_VISUAL[op.tool]}
                                                    onChange={(partial) =>
                                                        setOps((prev) =>
                                                            prev.map((o) => (o.id === op.id ? { ...o, options: { ...o.options, ...partial } } : o))
                                                        )
                                                    }
                                                />
                                                <p className="font-mono-tag text-[10px] text-gray-500 mt-1">
                                                    A grade reflete o PDF enviado. Em etapas posteriores, use os campos de texto (a numeração segue o documento naquele ponto do fluxo).
                                                </p>
                                            </div>
                                        ) : null}
                                        {!(i === 0 && FLOW_VISUAL[op.tool] && fl.entries.length === 1 &&
                                            (fl.entries[0].type === "application/pdf" || fl.entries[0].name.toLowerCase().endsWith(".pdf"))) && (
                                        <div className="grid sm:grid-cols-2 gap-3">
                                        {spec.options.map((opt) => (
                                            <div key={opt.key}>
                                                <label htmlFor={`flow-opt-${op.id}-${opt.key}`} className="block font-mono-tag text-[11px] font-bold uppercase tracking-wider text-black mb-1">
                                                    {opt.label}
                                                </label>
                                                {opt.type === "select" ? (
                                                    <select
                                                        id={`flow-opt-${op.id}-${opt.key}`}
                                                        value={op.options[opt.key] ?? opt.default}
                                                        onChange={(e) => setOpOption(op.id, opt.key, e.target.value)}
                                                        data-testid={`flow-option-${op.tool}-${opt.key}`}
                                                        className="w-full border-2 border-black bg-white px-3 py-2 text-sm"
                                                    >
                                                        {opt.choices.map((c) => (
                                                            <option key={c.value} value={c.value}>{c.label}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        id={`flow-opt-${op.id}-${opt.key}`}
                                                        type={opt.type === "password" ? "password" : "text"}
                                                        value={op.options[opt.key] ?? ""}
                                                        placeholder={opt.placeholder}
                                                        onChange={(e) => setOpOption(op.id, opt.key, e.target.value)}
                                                        data-testid={`flow-option-${op.tool}-${opt.key}`}
                                                        className="w-full border-2 border-black bg-white px-3 py-2 text-sm"
                                                    />
                                                )}
                                            </div>
                                        ))}
                                        </div>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ol>

                {clientWarnings().map((w) => (
                    <p key={w} className="mx-4 mb-3 border-2 border-black bg-[#FFFBEB] px-3 py-2 text-xs text-amber-900 font-medium" data-testid="flow-client-warning">
                        {w}
                    </p>
                ))}

                {/* resumo */}
                {ops.length > 0 && (
                    <div className="mx-4 mb-4 border-2 border-dashed border-gray-400 p-3" data-testid="flow-summary">
                        <span className="font-mono-tag text-[11px] font-bold uppercase text-gray-500">Resumo do fluxo</span>
                        <p className="text-sm text-black mt-1">
                            {fl.entries.length} arquivo(s) → {ops.map((o, i) => catalog?.operations.find((s) => s.key === o.tool)?.label || o.tool).join(" → ")} → 1 download
                        </p>
                    </div>
                )}

                <div className="px-4 pb-4">
                    <button
                        onClick={process}
                        disabled={!canProcess}
                        data-testid="flow-process-button"
                        className="w-full py-4 bg-[#0284C7] hover:bg-black disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-poster text-2xl tracking-wide transition-colors flex items-center justify-center gap-3"
                    >
                        {busy ? (
                            <>
                                <Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" />
                                {phase === "uploading" ? `Enviando… ${progress}%` : "Processando fluxo…"}
                            </>
                        ) : limitReached ? (
                            "LIMITE DIÁRIO ATINGIDO"
                        ) : (
                            `EXECUTAR FLUXO${ops.length ? ` (${ops.length} ETAPA${ops.length > 1 ? "S" : ""})` : ""}`
                        )}
                    </button>
                    {phase === "uploading" && (
                        <div className="mt-3 border-2 border-black h-6 bg-white" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} data-testid="flow-upload-progress">
                            <div className="h-full bg-[#22D3EE] transition-[width]" style={{ width: `${progress}%` }} />
                        </div>
                    )}
                </div>

                {/* erro geral */}
                {phase === "failed" && error && (
                    <div className="mx-4 mb-4 border-2 border-black bg-[#FEF2F2] p-4" role="alert" data-testid="flow-error-box">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
                            <div className="flex-1">
                                <p className="font-bold text-sm text-red-900">O fluxo não foi concluído</p>
                                <p className="text-xs text-red-800 mt-1">{error}</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <button onClick={retryFlow} data-testid="flow-retry-button" className="px-4 py-2 bg-black text-white font-mono-tag text-xs font-bold uppercase hover:bg-[#0284C7] transition-colors">
                                        Corrigir opções e reenviar
                                    </button>
                                    <button onClick={resetAll} data-testid="flow-reset-button" className="px-4 py-2 border-2 border-black bg-white font-mono-tag text-xs font-bold uppercase hover:bg-black hover:text-white transition-colors">
                                        Começar do zero
                                    </button>
                                </div>
                                <p className="mt-2 text-[11px] text-red-700">Seus arquivos continuam selecionados — ajuste as etapas e reenvie.</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* sucesso */}
                {phase === "done" && job && (
                    <div className="m-4 border-2 border-black bg-[#F0F9FF] p-8 text-center" data-testid="flow-success-box">
                        <ShieldCheck className="w-12 h-12 text-[#0284C7] mx-auto mb-3" aria-hidden="true" />
                        <p className="font-poster text-3xl text-black">FLUXO CONCLUÍDO!</p>
                        <p className="font-mono-tag text-xs text-gray-600 mt-2">
                            {job.result_name} · {job.result_size ? formatBytes(job.result_size) : ""} · expira em até 60 min
                        </p>
                        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                            <a
                                href={downloadUrl(job.job_id, token)}
                                download={job.result_name}
                                data-testid="flow-download-button"
                                className="px-8 py-4 bg-[#0284C7] hover:bg-black text-white font-poster text-xl tracking-wide transition-colors inline-flex items-center justify-center gap-2"
                            >
                                <Download className="w-5 h-5" aria-hidden="true" /> BAIXAR RESULTADO
                            </a>
                            <button
                                onClick={resetAll}
                                data-testid="flow-new-button"
                                className="px-8 py-4 border-2 border-black bg-white hover:bg-black hover:text-white text-black font-poster text-xl tracking-wide transition-colors inline-flex items-center justify-center gap-2"
                            >
                                <RotateCcw className="w-5 h-5" aria-hidden="true" /> NOVO FLUXO
                            </button>
                            {isPro && (
                                <button
                                    onClick={saveHistory}
                                    disabled={historySaved || historySaving}
                                    data-testid="flow-save-history-button"
                                    className="px-8 py-4 border-2 border-black bg-black text-[#22D3EE] disabled:opacity-60 font-poster text-xl tracking-wide transition-colors inline-flex items-center justify-center gap-2"
                                >
                                    {historySaving ? (
                                        <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                                    ) : (
                                        <Save className="w-5 h-5" aria-hidden="true" />
                                    )}
                                    {historySaved ? "SALVO NO HISTÓRICO" : historySaving ? "SALVANDO…" : "SALVAR NO HISTÓRICO"}
                                </button>
                            )}
                        </div>
                        {historyError && (
                            <p className="mt-3 text-xs text-red-700 font-medium" role="alert" data-testid="flow-history-error">{historyError}</p>
                        )}
                        {historySaved && (
                            <p className="mt-3 font-mono-tag text-[11px] text-gray-600" data-testid="flow-history-saved-note">
                                Salvo no seu histórico Pro. <a href="/historico" className="underline">Ver histórico</a>
                            </p>
                        )}
                    </div>
                )}
            </section>
        </main>
    );
}
